/**
 * JWT payload decoder for CLI superadmin detection.
 *
 * Decodes the payload section of a JWT locally (no signature verification,
 * no network calls). Used to conditionally show superadmin CLI commands.
 *
 * @module
 */

/**
 * Decode the payload (second segment) of a JWT without verifying the signature.
 * Returns the parsed JSON object, or `null` if the token is malformed.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const base64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const json = Buffer.from(base64, "base64").toString("utf8");
    const payload: unknown = JSON.parse(json);

    if (typeof payload !== "object" || payload === null || Array.isArray(payload)) {
      return null;
    }
    return payload as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Returns `true` when the JWT's `is_superadmin` claim is exactly `true`.
 * Returns `false` for any invalid, missing, or non-superadmin token.
 */
export function isSuperadminToken(token: string | null | undefined): boolean {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  return payload?.is_superadmin === true;
}
